import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Agentation } from 'agentation';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { BudgetModelProvider } from '@/analytics';
import { ApiError } from '@/api';
import { SessionProvider } from '@/auth';
import { AppShell } from '@/components/layouts';
import { ProjectDetailsRoute, SettingsRedirect } from '@/components/projectDetails';
import { AGENTATION_ENABLED, AGENTATION_ENDPOINT } from '@/config';
import { FinancialsPage } from '@/pages/FinancialsPage';
import { PlanningPage } from '@/pages/PlanningPage';
import { ProjectAreaPlaceholderPage } from '@/pages/ProjectAreaPlaceholderPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ResourcePlannerPage } from '@/pages/ResourcePlannerPage';
import { CallbackRoute } from '@/routes/CallbackRoute';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { theme } from '@/theme';

/**
 * Retry policy matches the Web repo: never retry a 4xx, since those are contract or
 * permission problems that a retry cannot fix.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

export const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        <BudgetModelProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/callback" element={<CallbackRoute />} />
              <Route element={<AppShell />}>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ProjectsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route element={<AppShell variant="full-bleed" />}>
                <Route
                  path="/resources"
                  element={
                    <ProtectedRoute>
                      <ResourcePlannerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/planning"
                  element={
                    <ProtectedRoute>
                      <PlanningPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/financials"
                  element={
                    <ProtectedRoute>
                      <FinancialsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/team"
                  element={
                    <ProtectedRoute>
                      <ProjectAreaPlaceholderPage area="team" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/billing"
                  element={
                    <ProtectedRoute>
                      <ProjectAreaPlaceholderPage area="billing" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/details"
                  element={
                    <ProtectedRoute>
                      <ProjectDetailsRoute />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsRedirect />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </BudgetModelProvider>
      </QueryClientProvider>
    </SessionProvider>
    {/* Dev-only, and rendered through its own portal — its position in the tree is immaterial. */}
    {AGENTATION_ENABLED && <Agentation endpoint={AGENTATION_ENDPOINT} />}
  </ThemeProvider>
);
